import { Grid, useEventCallback } from '@mui/material';
import { useNavigate } from 'react-router';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { useSearchParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { transformerElectoralCard } from 'front-office/utils/transformer-electoral';
import { ElectoralCard, SaveElectoralCard } from 'shell/models/person/electoral-card';
import AccordionPerson from 'front-office/components/release-electoral-card/person';
import { ReleaseElectoralCard } from 'front-office/view-models/release-electoral-card';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import AccordionCardData from 'front-office/components/release-electoral-card/electoral-card-data';
import { electoralValidationSchema } from 'front-office/constants/release-electoral-card-schema';
import { PmiButton } from 'shell/components/shared/button';
import AnomalieErrorModal from 'shell/components/shared/anomalie-modal';
import { useEffect, useState, useMemo } from 'react';
import { Anomaly } from 'front-office/view-models/common-certificate-form';
import { ElectoralErrorHandler } from 'shell/components/shared/electoral-error-modal';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { ReleaseElectoralCardCrumbs } from 'front-office/constants/release-electoral-card-crumbs';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { toast } from 'react-toastify';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const ReleaseElectoralCardComponent = (): React.ReactElement => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  // query params
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const name = queryParams.get('nome') || '';
  const surname = queryParams.get('cognome') || '';
  const personState = queryParams.get('stato') || '';

  const [modalBlockingError, setModalBlockingError] = useState({ onConfirm: false, open: true });
  const [modalNonBlockingError, setModalNonBlockingError] = useState(false);
  const [errorModalOpen, setErrorModal] = useState<boolean>(false);
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const [modalErrorElectoral, setModalErrorElectoral] = useState({ open: false, message: '' });

  const confirmCardData = useHttpClient<Blob>(
    `${__ELECTORAL_API_URL__}/persona/tessera-elettorale/controllo-dati-inseriti-e-stampa`,
    {
      options: {
        method: 'POST',
        errorCatching: true,
        loading: true,
      },
    }
  );

  const onSubmit = useEventCallback(() => {
    const data: SaveElectoralCard = {
      codiceFiscale: releaseElectoralCardForm.values.fiscalCode || undefined,
      numTesseraNuova: releaseElectoralCardForm.values.newCardNumber || undefined,
      dataRilascio: releaseElectoralCardForm.values.newReleaseDate || undefined,
      motivoRilascio: releaseElectoralCardForm.values.newReleaseReason || undefined,
      tesseraNuova: releaseElectoralCardForm.values.flagNewCard || undefined,
      tesseraArchviata: releaseElectoralCardForm.values.flagArchived || undefined,
      annullamentoPOTDESTE: releaseElectoralCardForm.values.flagPodest || undefined,
      numeroTesseraPrecedente: releaseElectoralCardForm.values.previousCardNumber || undefined,
      flagAIRE: releaseElectoralCardForm.values.flagAire || undefined,
      flagAutorizzazzione: releaseElectoralCardForm.values.flagAuthoriaztion || undefined,
      statoTesseraInStampa: releaseElectoralCardForm.values.printCardState || undefined,
      cognome: releaseElectoralCardForm.values.surname || undefined,
      nome: releaseElectoralCardForm.values.name || undefined,
      descrizioneLuogoNascita: releaseElectoralCardForm.values.placeOfBirth || undefined,
      dataNascita: releaseElectoralCardForm.values.dateOfBirth || undefined,
      nomeSindaco: releaseElectoralCardForm.values.mayorName || undefined,
      sezioneElettorale: releaseElectoralCardForm.values.electoralSection || undefined,
      indirizzoSezione: releaseElectoralCardForm.values.sectionAddress || undefined,
      nomeVia: releaseElectoralCardForm.values.addressName || undefined,
      numeroCivico: releaseElectoralCardForm.values.civic || undefined,
      barrato: releaseElectoralCardForm.values.crossed || undefined,
      zona: releaseElectoralCardForm.values.zone || undefined,
      collegioSenato: releaseElectoralCardForm.values.senate || undefined,
      collegioCamera: releaseElectoralCardForm.values.room || undefined,
      indicatoreListaE: releaseElectoralCardForm.values.indicatoreListaE,
      indicatoreListaC: releaseElectoralCardForm.values.indicatoreListaC,
      autorizzazioneDocumenti: releaseElectoralCardForm.values.autorizzazioneDocumenti,
    };

    (confirmCardData({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then((response) => {
      const pdfCertificate = response?.data;
      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Tessera Elettorale',
            open: true,
          });
        });
      }
      toast.success('Operazione eseguita con successo');
    });
  });

  const personFilterTable = useFilterTable<PersonSearchStorageVM>();

  const { userProfile } = useUserProfile();

  const [anpr] = useHttpFetch<HttpResponse<ElectoralCard>, ReleaseElectoralCard>(
    `${__ELECTORAL_API_URL__}/persona/tessera-elettorale/controlli`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          codiceFiscale: fiscalCode || undefined,
        },
      },
      transformer: (response) =>
        transformerElectoralCard(
          response,
          personFilterTable.values?.subject?.dati3002,
          userProfile
        ),
    }
  );

  const dataANPR = (anpr?.data as ReleaseElectoralCard) || {};

  const releaseElectoralCardForm = useFormik<ReleaseElectoralCard>({
    initialValues: (anpr?.data as ReleaseElectoralCard) || {
      fiscalCode: '',
      surname: '',
      withoutSurname: false,
      name: '',
      withoutName: false,
      dossier: '',
      electorState: '',
      inscriptionYear: '',
      section: '',
      sectionProgressive: '',
      generalListNumber: '',
      zone: '',
      room: '',
      senate: '',
      province: '',
      previousCardNumber: '',
      previousReleaseDate: '',
      previousReleaseReason: '',
      newCardNumber: '',
      newReleaseDate: '',
      newReleaseReason: '',
      previousCardState: '',
      flagNewCard: '',
      flagPodest: '',
      flagArchived: '',
    },
    enableReinitialize: true,
    validationSchema: electoralValidationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (
      releaseElectoralCardForm.values.blockError ||
      (anpr?.data as ReleaseElectoralCard)?.blockError
    ) {
      setModalBlockingError({ open: true, onConfirm: false });
    }
    if (
      releaseElectoralCardForm.values.nonBlockError ||
      (anpr?.data as ReleaseElectoralCard)?.nonBlockError
    ) {
      setModalNonBlockingError(true);
    }
  }, [
    (anpr?.data as ReleaseElectoralCard)?.blockError,
    (anpr?.data as ReleaseElectoralCard)?.nonBlockError,
    releaseElectoralCardForm.values.blockError,
    releaseElectoralCardForm.values.nonBlockError,
  ]);

  const handleChangeCard = useEventCallback(
    (key: keyof ReleaseElectoralCard, value: ReleaseElectoralCard[keyof ReleaseElectoralCard]) => {
      releaseElectoralCardForm.setValues({
        ...(releaseElectoralCardForm.values || {}),
        [key]: value,
      });
    }
  );

  const anomalieList: Array<Anomaly> = useMemo(() => {
    return (anpr?.data as ReleaseElectoralCard)?.anomalyList || [];
  }, [anpr?.data]);

  useEffect(() => {
    if (anomalieList?.length > 0) {
      setErrorModal(true);
    }
  }, [anomalieList]);

  useEffect(() => {
    if (dataANPR?.indicatoreListaC === '1' || dataANPR?.indicatoreListaE === '1') {
      setModalErrorElectoral({
        open: true,
        message: 'Elettore solo Comunali o Europee - utilizzare tessera Mod.73',
      });
    }
  }, [dataANPR?.indicatoreListaC, dataANPR?.indicatoreListaE]);

  return (
    <form onSubmit={releaseElectoralCardForm.handleSubmit}>
      <Breadcrumbs items={ReleaseElectoralCardCrumbs} />
      <SearchMessages
        type={MessageType.Warning}
        title={"C'è un warning"}
        messages={[{ id: 1, value: releaseElectoralCardForm.values.nonBlockError || '' }]}
        visibility={modalNonBlockingError}
        onChangeVisibility={() => setModalNonBlockingError(false)}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StickyDiv>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="contained" color="cdmBrandThird" type="submit" fullWidth>
                Conferma
              </PmiButton>
            </div>
            <div className="pmi-d-block">
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => navigate('/sportello/tessera-elettorale')}
              >
                Indietro
              </PmiButton>
            </div>
          </StickyDiv>
        </Grid>
        <Grid item xs={12} md={9}>
          <AccordionPerson
            values={releaseElectoralCardForm.values}
            fiscalCode={fiscalCode}
            name={name}
            surname={surname}
            personState={personState}
            onChange={handleChangeCard}
            errors={releaseElectoralCardForm.errors as FormikErrors<ReleaseElectoralCard>}
            touched={
              releaseElectoralCardForm.touched as unknown as FormikTouched<ReleaseElectoralCard>
            }
          />
          <AccordionCardData
            values={releaseElectoralCardForm.values}
            onChange={handleChangeCard}
            errors={releaseElectoralCardForm.errors as FormikErrors<ReleaseElectoralCard>}
            touched={
              releaseElectoralCardForm.touched as unknown as FormikTouched<ReleaseElectoralCard>
            }
          />
        </Grid>
      </Grid>
      {(anomalieList as Array<Anomaly>)?.length > 0 ? (
        <AnomalieErrorModal
          open={errorModalOpen}
          onClose={() => {
            setErrorModal(false);
          }}
          dataPersons={releaseElectoralCardForm.values.anomalyList as Array<Anomaly>}
        />
      ) : undefined}
      {releaseElectoralCardForm.values.blockError ? (
        <ElectoralErrorHandler
          open={modalBlockingError.open}
          onClose={() => {
            if (!modalBlockingError.onConfirm) {
              navigate('/sportello/tessera-elettorale');
            }
            setModalBlockingError({ open: false, onConfirm: false });
          }}
          message={releaseElectoralCardForm.values.blockError}
          title="Errore Bloccante"
        />
      ) : undefined}
      {modalErrorElectoral.open ? (
        <ElectoralErrorHandler
          open={modalErrorElectoral.open}
          onClose={() => {
            setModalErrorElectoral({ open: false, message: '' });
          }}
          message={modalErrorElectoral.message}
          title="Warning"
        />
      ) : undefined}
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Tessera Elettorale"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          navigate('/sportello/tessera-elettorale');
        }}
      />
    </form>
  );
};

ReleaseElectoralCardComponent.displayName = 'Release electoral card component';

export default ReleaseElectoralCardComponent;
